import styled from 'styled-components';

import {
  Input as IconInput,
  Label as IconLabel,
  LabelText as IconLabelText
} from 'components/IconCheckbox/IconCheckbox.styles';

import { FilterHint, FilterSeparator } from './steps/Audience/Audience.styles';
import {
  Divider as FiltersDivider,
  FilterOptionsContainer
} from './steps/Audience/AudienceFilters';
import {
  FilterFormAudienceWrapper,
  Title as FilterBlockTitle
} from './steps/Audience/components/FilterFormAudienceWrapper';
import { CategoryContainer } from './steps/Audience/components/TargetingCriteria';

/*
  The Create Audience modal as the Orbit builder frame draws it: two stacked cards and a footer bar
  on the scrim, dark filter tiles and a dark criteria panel. Only the embedded form wears it; the
  routed audience pages keep their own look. Doubled class selectors outrank the dark scope's card
  rules, which sit at the same depth.
*/
const EmbeddedAudienceSkin = styled.div`
  && .ant-card {
    margin: 0 0 18px;
    border: 1px solid var(--dk-border-subtle) !important;
    border-radius: 16px !important;
    background: var(--dk-surface-card);
  }

  && [data-testid='bottom-bar-inline'] {
    margin-top: 0;
  }

  && .ant-card-head {
    min-height: 0;
    padding: 22px 26px 0 13px;
    border-bottom: 0;
    background: transparent;
  }

  && .ant-card-head-title {
    padding: 3px 0 0;
    color: var(--dk-text-primary);
    font-size: 25px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.5px;
  }

  && .ant-card + .ant-card .ant-card-head {
    padding: 22px 26px 0;
  }

  && .ant-card + .ant-card .ant-card-head-title {
    font-size: 18px;
    letter-spacing: -0.18px;
  }

  && .ant-card-body {
    padding: 22px 26px 26px;
  }

  && .ant-form-item {
    margin-bottom: 0;
  }

  && .ant-form-item-label > label {
    color: var(--dk-text-secondary);
    font-size: 14.5px;
  }

  && .ant-form-item-required::before {
    color: var(--dk-danger) !important;
  }

  && .ant-input-affix-wrapper {
    height: 49px;
    padding: 13px 16px;
    border: 1px solid var(--dk-border-subtle);
    border-radius: 10px;
    background: var(--dk-surface-raised);
  }

  && .ant-input-affix-wrapper .ant-input {
    background: transparent;
    color: var(--dk-text-primary);
    font-size: 14.5px;
  }

  ${FilterOptionsContainer} {
    flex: 0 0 640px;
    max-width: 640px;
    padding-right: 26px;

    h5.ant-typography {
      margin-bottom: 4px;
      color: var(--dk-text-primary);
      font-size: 15px;
      font-weight: 700;
    }
  }

  ${FilterHint} {
    color: var(--dk-text-muted);
    font-size: 12.5px;
    line-height: 19.38px;
  }

  ${FiltersDivider} {
    border-left-color: var(--dk-border-subtle);
  }

  ${CategoryContainer} h5.ant-typography {
    margin: 0 0 10px;
    color: var(--dk-text-muted);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 1.1px;
    text-transform: uppercase;
  }

  ${CategoryContainer} > div {
    gap: 10px;
  }

  ${IconLabel} {
    width: 76px;
    height: 76px;
    gap: 7px;
    justify-content: center;
    padding: 6px 4px;
    border: 1px solid var(--dk-border-subtle);
    border-radius: 12px;
    background: var(--dk-surface-raised);

    img {
      margin: 0;
      filter: brightness(0) invert(0.75);
    }
  }

  ${IconLabelText} {
    color: var(--dk-text-secondary);
    font-size: 8.5px;
    font-weight: 800;
    line-height: 10.2px;
    letter-spacing: 0.6px;
  }

  ${IconInput}:hover + ${IconLabel} {
    background: var(--dk-surface-hover);
  }

  ${IconInput}:checked + ${IconLabel} {
    border-color: var(--dk-link);
    background: rgba(38, 138, 255, 0.14);

    img {
      filter: none;
    }

    ${IconLabelText} {
      color: var(--dk-link-hover);
    }
  }

  ${FilterFormAudienceWrapper} {
    padding: 22px 24px;
    border: 1px solid var(--dk-border-subtle);
    border-radius: 14px;
    background: var(--dk-surface-raised);
    color: var(--dk-text-primary);

    > div:first-child {
      width: 62px;
    }

    img {
      width: 21px;
      height: 21px;
      filter: brightness(0) invert(0.45);
    }
  }

  ${FilterBlockTitle} {
    margin-top: 6px;
    color: var(--dk-text-muted);
    font-size: 8.5px;
    font-weight: 800;
    letter-spacing: 0.6px;
  }

  ${FilterSeparator} {
    color: var(--dk-text-muted);
  }

  ${FilterFormAudienceWrapper} .ant-divider {
    border-top-color: var(--dk-border-subtle);
  }

  ${FilterFormAudienceWrapper} .ant-form-item-label > label,
  ${FilterFormAudienceWrapper} label {
    color: var(--dk-text-secondary);
    font-size: 13px;
  }

  ${FilterFormAudienceWrapper} .ant-select-selector,
  ${FilterFormAudienceWrapper} .ant-input,
  ${FilterFormAudienceWrapper} .ant-input-number,
  ${FilterFormAudienceWrapper} .ant-picker {
    border: 1px solid var(--dk-border-subtle) !important;
    border-radius: 9px !important;
    background: var(--dk-surface-card) !important;
    color: var(--dk-text-primary);
  }

  ${FilterFormAudienceWrapper} .ant-input-affix-wrapper {
    border: 1px solid var(--dk-border-subtle) !important;
    border-radius: 9px !important;
    background: var(--dk-surface-card) !important;
  }

  ${FilterFormAudienceWrapper} .ant-input-affix-wrapper .ant-input {
    border: 0 !important;
    background: transparent !important;
  }

  ${FilterFormAudienceWrapper} [data-locked='true'] .ant-select-selector,
  ${FilterFormAudienceWrapper} [data-locked='true'] .ant-input-affix-wrapper,
  ${FilterFormAudienceWrapper} [data-locked='true'] .ant-input {
    border-style: dashed !important;
    background: #0a1122 !important;
    color: var(--dk-text-secondary);
  }

  ${FilterFormAudienceWrapper} .ant-btn-dashed {
    height: 42px;
    padding: 12px 17px;
    border: 1px dashed var(--dk-border-subtle);
    border-radius: 9px;
    background: transparent;
    color: var(--dk-link);
    font-size: 13.5px;
    font-weight: 700;
  }

  [data-testid='audience-custom-count'] {
    color: var(--dk-text-muted);
    font-size: 13px;

    strong {
      color: var(--dk-text-primary);
    }
  }
`;

export default EmbeddedAudienceSkin;
